import type { MultiSelectChrome } from '@/lib/transforms/types/multi-select-chrome'
import type { ShapeBounds } from '@/lib/shapes/types/shape-bounds'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import {
  boxVerticesForShapeGeometry,
  canvasVecToLocal,
  localVecToCanvas,
} from '@/lib/transforms/geometry'
import type { UnifiedSelectionResizeHandle } from '@/lib/transforms/types/unified-selection-resize-handle'
import type { MultiSelectResizeShapeSnap } from '../types/multi-select-resize-shape-snap'
import { MIN_DIMENSION } from '../constants/multi-resize-min-dimension'

export function unionLocalBoundsForMultiResizePreviewByVertices(
  chrome: MultiSelectChrome,
  shapeInitials: Record<string, MultiSelectResizeShapeSnap>,
  updates: Record<string, Partial<ShapeData>>
): { minX: number; minY: number; maxX: number; maxY: number } | null {
  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity
  let hasPoint = false

  for (const id of Object.keys(shapeInitials)) {
    const initial = shapeInitials[id]
    const update = updates[id]

    if (!initial || !update) continue

    const nextX = update.x ?? initial.x
    const nextY = update.y ?? initial.y
    const nextWidth = update.width ?? initial.width
    const nextHeight = update.height ?? initial.height

    const vertices = boxVerticesForShapeGeometry({
      x: nextX,
      y: nextY,
      width: nextWidth,
      height: nextHeight,
      rotation: initial.rotation ?? 0,
    })

    for (const vertex of vertices) {
      const local = canvasVecToLocal(
        { x: vertex.x - chrome.pivotX, y: vertex.y - chrome.pivotY },
        chrome.rotationDeg
      )

      minX = Math.min(minX, local.x)
      minY = Math.min(minY, local.y)
      maxX = Math.max(maxX, local.x)
      maxY = Math.max(maxY, local.y)
      hasPoint = true
    }
  }

  return hasPoint ? { minX, minY, maxX, maxY } : null
}

export function applyMultiResizeCoverageCorrection(
  nextChrome: MultiSelectChrome,
  direction: UnifiedSelectionResizeHandle,
  shapeInitials: Record<string, MultiSelectResizeShapeSnap>,
  _boundsInitials: Record<string, ShapeBounds | undefined> | undefined,
  updates: Record<string, Partial<ShapeData>>
): MultiSelectChrome {
  const unionLocal = unionLocalBoundsForMultiResizePreviewByVertices(
    nextChrome,
    shapeInitials,
    updates
  )

  if (!unionLocal) return nextChrome
  const EPS = 1e-6
  let minX = -nextChrome.width / 2
  let maxX = nextChrome.width / 2
  let minY = -nextChrome.height / 2
  let maxY = nextChrome.height / 2

  const overflowLeft = Math.max(0, minX - unionLocal.minX - EPS)
  const overflowRight = Math.max(0, unionLocal.maxX - maxX - EPS)
  const overflowTop = Math.max(0, minY - unionLocal.minY - EPS)
  const overflowBottom = Math.max(0, unionLocal.maxY - maxY - EPS)

  if (overflowLeft <= EPS && overflowRight <= EPS && overflowTop <= EPS && overflowBottom <= EPS) {
    return nextChrome
  }

  if (direction.includes('e')) {
    maxX += overflowRight
  } else if (direction.includes('w')) {
    minX -= overflowLeft
  } else {
    const growX = Math.max(overflowLeft, overflowRight)

    minX -= growX
    maxX += growX
  }

  if (direction.includes('s')) {
    maxY += overflowBottom
  } else if (direction.includes('n')) {
    minY -= overflowTop
  } else {
    const growY = Math.max(overflowTop, overflowBottom)

    minY -= growY
    maxY += growY
  }

  const correctedWidth = Math.max(MIN_DIMENSION, maxX - minX)
  const correctedHeight = Math.max(MIN_DIMENSION, maxY - minY)
  const centerOffsetLocal = {
    x: (minX + maxX) / 2,
    y: (minY + maxY) / 2,
  }
  const centerOffsetCanvas = localVecToCanvas(centerOffsetLocal, nextChrome.rotationDeg)

  return {
    ...nextChrome,
    pivotX: nextChrome.pivotX + centerOffsetCanvas.x,
    pivotY: nextChrome.pivotY + centerOffsetCanvas.y,
    width: correctedWidth,
    height: correctedHeight,
  }
}
